import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, Stat, LoadingShell, SectionCard } from '@/components/ui-bits';
import { fmtRelative } from '@/lib/format';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import {
  MessageSquare, BarChart3, UserPlus, Users, Target, Inbox, CheckSquare, Clock,
} from 'lucide-react';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const { data: d } = await api.get('/dashboard');
      if (alive) setData(d);
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data) return <LoadingShell />;
  const k = data.kpis;
  const fmtWait = (sec) => {
    if (!sec || sec < 60) return `${sec || 0}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    return `${Math.floor(sec / 3600)}h`;
  };

  return (
    <div>
      <PageHeader
        title="Inicio"
        actions={(
          <>
            <Link to="/app/inbox" className="btn-secondary h-9 text-xs">
              <MessageSquare className="h-3.5 w-3.5" /> Inbox
            </Link>
            <Link to="/app/reports" className="btn-primary h-9 text-xs">
              <BarChart3 className="h-3.5 w-3.5" /> Reportes
            </Link>
          </>
        )}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Nuevos 24h" value={k.leads_today} icon={UserPlus} />
        <Stat label="Leads" value={k.leads_total} icon={Users} />
        <Stat label="Conversión" value={`${k.conversion_rate}%`} icon={Target} />
        <Stat label="Mensajes 24h" value={k.messages_today} icon={MessageSquare} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Link to="/app/inbox?filter=waiting">
          <Stat label="En espera" value={k.inbox_waiting ?? 0} icon={Inbox} />
        </Link>
        <Link to="/app/inbox?filter=resolved">
          <Stat label="Resueltas" value={k.inbox_resolved_today ?? 0} icon={CheckSquare} />
        </Link>
        <Stat label="CSAT" value={k.csat_avg_week != null ? k.csat_avg_week : '—'} icon={Target} />
        <Stat label="Tareas vencidas" value={k.tasks_overdue} icon={Clock} />
      </div>

      <div className="grid lg:grid-cols-5 gap-3 mt-5">
        <SectionCard
          className="lg:col-span-3"
          title="Pipeline"
          action={(
            <span className="text-xs text-muted-foreground tabular-nums">
              {data.funnel.reduce((a, s) => a + s.count, 0)}
            </span>
          )}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 12,
                    fontSize: 12,
                    boxShadow: 'var(--shadow-sm)',
                  }}
                />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard
          className="lg:col-span-2"
          title="Cola"
          action={<Link to="/app/inbox?filter=waiting" className="text-xs font-medium text-primary">Ver</Link>}
          padded={false}
        >
          <div className="max-h-56 overflow-y-auto">
            {(data.waiting_top || []).length === 0 && (
              <div className="text-sm text-muted-foreground p-6 text-center">Vacía</div>
            )}
            {(data.waiting_top || []).map((w) => (
              <Link
                key={w.lead_id}
                to={`/app/inbox?lead=${w.lead_id}&filter=waiting`}
                className="list-row flex items-center gap-2.5"
              >
                <div className="h-8 w-8 rounded-full bg-accent text-primary grid place-items-center text-[11px] font-semibold shrink-0">
                  {(w.name || '?')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{w.name}</div>
                </div>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {fmtWait(w.wait_seconds)}
                </span>
              </Link>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <SectionCard title="Agentes" padded={false}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-2 py-2.5 text-right">Leads</th>
                <th className="px-2 py-2.5 text-right">Cerrados</th>
                <th className="px-4 py-2.5 text-right">Tareas</th>
              </tr>
            </thead>
            <tbody>
              {data.by_agent.map((a) => (
                <tr key={a.id} data-testid="agent-row" className="border-b border-border/50 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="font-medium truncate max-w-[180px]">{a.name || a.email}</div>
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{a.leads}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{a.closed}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{a.open_tasks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>

        <SectionCard title="Actividad" padded={false}>
          <div className="max-h-64 overflow-y-auto">
            {data.recent_activities.slice(0, 8).map((a) => (
              <div key={a.id} className="list-row flex items-center gap-2.5">
                <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <div className="flex-1 min-w-0 text-[13px] truncate">{a.message}</div>
                <span className="text-[11px] text-muted-foreground shrink-0">
                  {fmtRelative(a.created_at)}
                </span>
              </div>
            ))}
            {data.recent_activities.length === 0 && (
              <div className="text-sm text-muted-foreground p-6 text-center">Sin actividad</div>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
